import React, { useState } from 'react';
import { X, Package, AlertTriangle, Loader } from 'lucide-react';

const PlaceOrderModal = ({ items, onClose, onConfirm, loading }) => {
  const [division] = useState('Hydraulics');
  const [freightType, setFreightType] = useState('Airbag');

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(items, freightType);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-brand sm:mx-0 sm:h-10 sm:w-10">
                    <Package className="h-6 w-6 text-black" />
                  </div>
                  <h3 className="ml-3 text-lg leading-6 font-medium text-gray-900">
                    Place Items on OrderBoard
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="mt-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-yellow-400" />
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        This will create {items.length} {items.length === 1 ? 'item' : 'items'} in the <strong>{division}</strong> division on OrderBoard.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Freight Type
                  </label>
                  <select
                    value={freightType}
                    onChange={(e) => setFreightType(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand focus:border-brand text-gray-900"
                    disabled={loading}
                  >
                    <option value="Airbag">Airbag (Urgent)</option>
                    <option value="Roadfreight">Roadfreight (Standard)</option>
                  </select>
                </div>

                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Items to Place:</h4>
                  <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Customer
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Order #
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Part Number
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Description
                          </th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                            Qty
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {items.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {item.customer_name}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {item.order_number}
                            </td>
                            <td className="px-4 py-2 text-sm font-mono text-gray-900">
                              {item.part_number}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 max-w-xs truncate" title={item.description}>
                              {item.description || '-'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 text-center">
                              {item.backorder_qty}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-xs text-blue-700">
                    <strong>Note:</strong> Each item will include customer name and order number in the notes field for reference.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-black hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin h-4 w-4 mr-2" />
                    Placing on OrderBoard...
                  </>
                ) : (
                  <>Place {items.length} {items.length === 1 ? 'Item' : 'Items'} on OrderBoard</>
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PlaceOrderModal;
